// NetworkPage — who works with whom, redesigned (docs/R5-NETWORK-DESIGN.md). Three instruments
// for the operator's three questions, all read from ONE payload (`GET /api/network/flows`):
//
//   Payers     one row per counterpart, sorted by the people it accounts for. The coverage bar is
//              a LENGTH on a zero baseline against the channel's own volume
//              (`channel.measure_participants`); the dotted rest is what nobody has accounted for
//   Providers  one row per organisation naming links; its payer mix as a 100 % stacked bar, the
//              unattributed rest dotted
//   Matrix     providers × payers; a cell's ink is the share — the dense record as a table that
//              is read across rows and down columns, never as a hairball
//
// The node-link graph survives only as the FOCUSED neighbourhood — one organisation and its
// links — beside that organisation's own Network zone. NOTHING IS CREATED HERE: the mast carries
// no `.btn-accent`; a link is declared on an organisation's own Network zone or arrives as a
// proposal a research run files. This page reads, filters, sorts and focuses.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import NetGraph from '../components/NetGraph.js';
import NetworkZone from '../components/NetworkZone.js';
import FlowMatrix from '../components/network/FlowMatrix.js';
import {
  Att, Bar, Fig, Lnk, Mast, PageShell, Rail, RailCell, Ref, Sep, Sheet, Stack, Tag, Tbl, Zone, ZoneHead,
  type StackSegment,
} from '../components/PageKit.js';
import { useLocale, useT } from '../i18n/index.js';
import { ROUTES, open as openRoute, useDeepLink, useRouteParams } from '../routes.js';
import { getFlows, getNetwork, type FlowFilters } from '../api/network.js';
import { listOrganisations } from '../api/organisations.js';
import { RASTER, labelFor, type RasterLang } from '../../shared/raster.v1.js';
import {
  LINK_ROLES, QUALIFIER_SIGN, REGIME_TOKENS, linkLabel, quantityText,
  type FlowFacetDim, type FlowPayer, type FlowProvider, type FlowsResponse, type LinkRole, type NetLang,
  type NetworkResponse, type RegimeToken,
} from '../../shared/network.v1.js';
import './NetworkPage.de.js';
import './NetworkPage.fr.js';

const CANTONS = ['VD', 'GE', 'FR', 'NE', 'VS', 'JU', 'CH'];
const SUBSECTOR_CELL = RASTER.find((b) => b.key === 'identity')?.cells.find((c) => c.key === 'subsector');
const SUBSECTORS = (SUBSECTOR_CELL?.values ?? []).map((v) => v.value);

/** The counterpart roles that move people or money — the Payers view and the matrix count these. */
const PAYER_ROLES: readonly LinkRole[] = ['payer', 'referrer', 'funder'];

type View = 'payers' | 'providers' | 'matrix';
const VIEWS: View[] = ['payers', 'providers', 'matrix'];
type Dim = FlowFacetDim;
const DIMS: Dim[] = ['role', 'canton', 'regime', 'subsector', 'year'];

interface Filters {
  role?: LinkRole;
  canton?: string;
  regime?: RegimeToken;
  subsector?: string;
  year?: number;
  heldOnly?: boolean;
  group?: 'regime';
}

type PayerSortKey = 'people' | 'coverage' | 'providers' | 'name' | 'year';
type ProviderSortKey = 'payers' | 'attributed' | 'clients' | 'name' | 'year';
interface Sort<K extends string> { key: K; dir: 'asc' | 'desc' }

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/** `null` sorts after every number in either direction — "absent" is not "smallest". */
function cmpNum(a: number | null, b: number | null, dir: 'asc' | 'desc'): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return dir === 'desc' ? b - a : a - b;
}

export default function NetworkPage() {
  const t = useT();
  const { lang, fmtNum, fmtDate } = useLocale();
  const nl = lang as NetLang;
  const params = useRouteParams();

  const [view, setView] = useState<View>('payers');
  const [filters, setFilters] = useState<Filters>({});
  const [openDim, setOpenDim] = useState<Dim | null>(null);
  const [focus, setFocus] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<Array<{ id: number; name: string; ref: string | null }>>([]);

  const [flows, setFlows] = useState<FlowsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ego, setEgo] = useState<NetworkResponse | null>(null);

  const [payerSort, setPayerSort] = useState<Sort<PayerSortKey>>({ key: 'people', dir: 'desc' });
  const [providerSort, setProviderSort] = useState<Sort<ProviderSortKey>>({ key: 'payers', dir: 'desc' });

  useDeepLink(params, {
    focus: (v) => { const n = Number(v); if (Number.isInteger(n) && n > 0) setFocus(n); },
    view: (v) => { if ((VIEWS as string[]).includes(v)) setView(v as View); },
    role: (v) => setFilters((f) => ({ ...f, role: v as LinkRole })),
    canton: (v) => setFilters((f) => ({ ...f, canton: v })),
    regime: (v) => setFilters((f) => ({ ...f, regime: v as RegimeToken })),
    year: (v) => { const n = Number(v); if (Number.isInteger(n)) setFilters((f) => ({ ...f, year: n })); },
    group: (v) => { if (v === 'regime') setFilters((f) => ({ ...f, group: 'regime' })); },
  });

  // ── the one payload ────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    try {
      const body: FlowFilters = {
        role: filters.role, canton: filters.canton, regime: filters.regime, subsector: filters.subsector,
        year: filters.year, held_only: filters.heldOnly, group: filters.group,
      };
      setFlows(await getFlows(body));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [filters]);
  useEffect(() => { void load(); }, [load]);

  // ── the focused neighbourhood — fetched only when something is focused ─────
  useEffect(() => {
    if (focus == null) { setEgo(null); return; }
    let live = true;
    getNetwork({ focus }).then((r) => { if (live) setEgo(r); }).catch(() => { if (live) setEgo(null); });
    return () => { live = false; };
  }, [focus]);

  // ── the typeahead: find an organisation to FOCUS, not a substring to filter by ──
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setMatches([]); return; }
    let live = true;
    const id = setTimeout(() => {
      listOrganisations({ q }).then((r) => {
        if (live) setMatches(r.items.slice(0, 8).map((o) => ({ id: o.id, name: o.name, ref: o.ref })));
      }).catch(() => { if (live) setMatches([]); });
    }, 200);
    return () => { live = false; clearTimeout(id); };
  }, [query]);

  const setFilter = (dim: Dim, value: string | null) => {
    setFilters((f) => {
      const next: Filters = { ...f };
      if (value === null) delete next[dim];
      else if (dim === 'year') next.year = Number(value);
      else (next as Record<string, unknown>)[dim] = value;
      return next;
    });
  };
  const anyFilter = DIMS.some((d) => filters[d] != null) || !!filters.heldOnly || !!filters.group;
  const clearFilters = () => { setFilters({}); setOpenDim(null); };

  const dimLabel = (dim: Dim): string => (
    dim === 'role' ? t('Role') : dim === 'canton' ? t('Canton') : dim === 'regime' ? t('Regime') : dim === 'subsector' ? t('Sub-sector') : t('Year')
  );
  const valueLabel = (dim: Dim, value: string): string => {
    if (dim === 'role') return linkLabel(nl, 'role', value);
    if (dim === 'regime') return linkLabel(nl, 'regime', value);
    if (dim === 'subsector') return labelFor(lang as RasterLang, 'identity', 'subsector', value);
    return value;
  };
  const dimValues = (dim: Dim): string[] => {
    const facet = flows?.facets[dim] ?? {};
    const vocabulary: string[] = dim === 'role' ? [...LINK_ROLES]
      : dim === 'canton' ? CANTONS
        : dim === 'regime' ? [...REGIME_TOKENS]
          : dim === 'subsector' ? SUBSECTORS
            : Object.keys(facet).sort((a, b) => Number(b) - Number(a));
    // the counted values first, by how many rows they keep — the eye lands on the live slice
    const counted = vocabulary.filter((v) => (facet[v] ?? 0) > 0).sort((a, b) => (facet[b] ?? 0) - (facet[a] ?? 0));
    const rest = vocabulary.filter((v) => !(facet[v] > 0));
    return [...counted, ...rest];
  };

  const summary = flows?.summary ?? null;
  const yearWord = filters.year != null ? String(filters.year) : null;

  // ── the payer rows, both views of them ─────────────────────────────────────
  const payerRows = useMemo(() => {
    const all = flows?.payers ?? [];
    const isPayer = (p: FlowPayer) => p.roles.some((r) => PAYER_ROLES.includes(r));
    const sortFn = (a: FlowPayer, b: FlowPayer): number => {
      const { key, dir } = payerSort;
      let c = 0;
      if (key === 'people') c = cmpNum(a.people_accounted, b.people_accounted, dir);
      else if (key === 'coverage') c = cmpNum(a.coverage_pct, b.coverage_pct, dir);
      else if (key === 'providers') c = cmpNum(a.providers, b.providers, dir);
      else if (key === 'year') c = cmpNum(a.year, b.year, dir);
      else c = dir === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      return c || cmpNum(a.people_accounted, b.people_accounted, 'desc') || cmpNum(a.providers, b.providers, 'desc') || a.name.localeCompare(b.name);
    };
    return { payers: all.filter(isPayer).sort(sortFn), others: all.filter((p) => !isPayer(p)).sort(sortFn) };
  }, [flows, payerSort]);
  const maxPeople = useMemo(() => payerRows.payers.reduce((m, p) => Math.max(m, p.people_accounted ?? 0), 0), [payerRows]);
  // The coverage column exists only when at least one payer holds a denominator; thirteen rows
  // each saying "volume not collected" is noise, one line in the foot says it once.
  const anyVolume = useMemo(() => payerRows.payers.some((p) => p.channel_volume != null || (p.members ?? []).some((m) => m.channel_volume != null)), [payerRows]);

  const providerRows = useMemo(() => {
    const all = flows?.providers ?? [];
    const { key, dir } = providerSort;
    return [...all].sort((a, b) => {
      let c = 0;
      if (key === 'payers') c = cmpNum(a.payers, b.payers, dir);
      else if (key === 'attributed') c = cmpNum(a.attributed_pct, b.attributed_pct, dir);
      else if (key === 'clients') c = cmpNum(a.clients_per_year?.value ?? null, b.clients_per_year?.value ?? null, dir);
      else if (key === 'year') c = cmpNum(a.latest_year, b.latest_year, dir);
      else c = dir === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      return c || cmpNum(a.payers, b.payers, 'desc') || a.name.localeCompare(b.name);
    });
  }, [flows, providerSort]);

  const focused = useMemo(() => {
    if (focus == null || !flows) return null;
    const asPayer = flows.payers.find((p) => p.organisation_id === focus)
      ?? flows.payers.flatMap((p) => p.members ?? []).find((m) => m.organisation_id === focus);
    const asProvider = flows.providers.find((p) => p.organisation_id === focus);
    const egoNode = ego?.nodes.find((n) => n.organisation_id === focus);
    const name = asPayer?.name ?? asProvider?.name ?? egoNode?.name ?? null;
    const ref = asPayer?.ref ?? asProvider?.ref ?? egoNode?.ref ?? null;
    return { asPayer: asPayer ?? null, asProvider: asProvider ?? null, name, ref };
  }, [focus, flows, ego]);

  const th = <K extends string>(label: ReactNode, key: K | null, sort: Sort<K>, set: (s: Sort<K>) => void, num?: boolean) => {
    if (!key) return <th className={num ? 'num' : undefined}>{label}</th>;
    const active = sort.key === key;
    return (
      <th
        className={cx(num && 'num', 'sortable')}
        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
        onClick={() => set({ key, dir: active && sort.dir === 'desc' ? 'asc' : 'desc' })}
        title={t('Sort by this column')}
      >
        {label}{active && <span className="sort-mark" aria-hidden="true">{sort.dir === 'desc' ? '▾' : '▴'}</span>}
      </th>
    );
  };

  const claimCell = (held: number, proposed: number, estimated: number) => (
    <>
      {held > 0 && <><Att kind="held" /> <Fig>{fmtNum(held)}</Fig></>}
      {proposed > 0 && <>{held > 0 ? ' ' : ''}<Att kind="proposed" /> <Fig>{fmtNum(proposed)}</Fig></>}
      {estimated > 0 && <> <Tag tone="ghost">{t('estimated')} <Fig>{fmtNum(estimated)}</Fig></Tag></>}
    </>
  );

  // §5.4: when a payer named BOTH a budget and a comptes amount for one year, the Σ took the
  // Comptes rows only — the cell says so, and says where the budget row still reads. The route
  // sends the sentence as the key it is (English IS the key), so the console translates it.
  const amountTitleOf = (p: FlowPayer): string | undefined => {
    const title = (p as FlowPayer & { amount_chf_title?: string | null }).amount_chf_title;
    return title ? t(title) : undefined;
  };

  // ── one payer line, and its member sub-rows when grouped by regime ──────────
  const payerLine = (p: FlowPayer, sub = false): ReactNode => {
    const vol = p.channel_volume;
    const clickable = p.organisation_id != null;
    const extraRoles = p.roles.filter((r) => r !== 'payer');
    const coverageLabel: ReactNode = vol
      ? p.people_accounted != null
        ? <>{p.coverage_pct} % {t('of')} {fmtNum(vol.value)}{vol.partial ? ' +' : ''}</>
        : <>{t('none of {m} accounted', { m: fmtNum(vol.value) })}</>
      : <span className="fv--unknown">{t('volume not collected')}</span>;
    const coverageTitle = vol
      ? (p.coverage_title ?? t('the channel counts {m} people in a measure ({when}); no provider gives a count yet', {
        m: fmtNum(vol.value), when: vol.year != null ? String(vol.year) : fmtDate(vol.as_of),
      }))
      : t('this body holds no channel.measure_participants cell — the bar has no denominator until a run reads one');
    return (
      <tr
        key={p.key}
        className={cx(clickable && 'row--click', sub && 'row--sub', focus != null && focus === p.organisation_id && 'row--focus')}
        onClick={clickable ? () => setFocus(p.organisation_id) : undefined}
      >
        <td>
          {p.kind === 'organisation'
            ? <><Ref>{p.ref}</Ref> {p.name}</>
            : <>{p.name} <Tag tone="ghost">{p.kind === 'regime' ? (p.members?.length ? t('{n} bodies folded', { n: p.members.length }) : t('regime, no body named')) : t('residual line')}</Tag></>}
          <span className="fd">
            {[...p.regimes.map((r) => linkLabel(nl, 'regime', r)), p.canton, ...extraRoles.map((r) => linkLabel(nl, 'role', r))].filter(Boolean).join(' · ') || ' '}
          </span>
        </td>
        <td className="num">
          <Fig>{fmtNum(p.providers)}</Fig>
          {p.providers > 0 && p.providers_counted < p.providers && (
            <span className="fd">{t('{n} give a count', { n: fmtNum(p.providers_counted) })}</span>
          )}
        </td>
        <td className="num">
          {p.people_accounted != null
            ? <Fig title={p.people_title ?? undefined}>{fmtNum(p.people_accounted)}</Fig>
            : <span className="fv--unknown">{t('not collected')}</span>}
          {p.people_accounted != null && maxPeople > 0 && (
            <Bar value={p.people_accounted} max={maxPeople} fill="computed" title={p.people_title ?? undefined} />
          )}
        </td>
        {anyVolume && (
          <td>
            <Bar
              value={vol ? p.people_accounted : null}
              max={vol ? vol.value : null}
              rest={vol ? 'unknown' : 'none'}
              fill={p.estimated > 0 && p.estimated === p.held ? 'estimated' : 'held'}
              title={coverageTitle}
              label={coverageLabel}
            />
          </td>
        )}
        <td className="num">{p.places_sum != null && <Fig>{fmtNum(p.places_sum)}</Fig>}</td>
        <td className="num">{p.amount_chf_sum != null && <Fig title={amountTitleOf(p)}>CHF {fmtNum(p.amount_chf_sum)}</Fig>}</td>
        <td className="num">{p.year != null ? <Fig>{p.year}</Fig> : <span className="fv--unknown">{t('not collected')}</span>}</td>
        <td>{claimCell(p.held, p.proposed, p.estimated)}</td>
      </tr>
    );
  };

  const mixOf = (p: FlowProvider): ReactNode => {
    if (!p.segments.length) return <span className="fv--unknown">{t('no mix on the record')}</span>;
    if (p.mix_basis === 'amount_chf' && p.segments.every((s) => s.share_computed == null)) {
      return <span className="fd">{t('{n} amounts read, no same-year revenue to divide by', { n: p.segments.length })}</span>;
    }
    const segments: StackSegment[] = p.segments
      .filter((s) => (s.share_computed ?? s.quantity) > 0)
      .map((s) => ({
        key: `${s.link_id}`,
        value: s.share_computed ?? s.quantity,
        fill: s.method === 'estimated' ? 'estimated' : s.share_computed != null ? 'computed' : 'held',
        label: `${Math.round(s.share_computed ?? s.quantity)}`,
        title: `${s.name} · ${p.mix_basis ? quantityText(nl, p.mix_basis, s.quantity, s.qualifier) : ''}${s.share_computed != null ? ` · ${s.share_computed} % ${t('of revenue')}` : ''}${s.method === 'estimated' ? ` · ${t('estimated')}` : ''}`,
      }));
    const basisWord = p.mix_basis === 'revenue_pct' || p.mix_basis === 'amount_chf' ? t('of revenue') : t('of participants');
    return (
      <Stack
        segments={segments}
        title={t('{name}: {basis}, {year} — {n} payers named, {a} % attributed, the dotted rest unattributed', {
          name: p.name, basis: basisWord, year: p.mix_year ?? '', n: segments.length, a: p.attributed_pct ?? '',
        })}
      />
    );
  };

  return (
    <PageShell>
      <Mast
        kicker={t('Network')}
        title={t('Who works with whom')}
        sub={summary ? (
          <>
            <Fig>{fmtNum(summary.payers)}</Fig> {t('payers')} <Sep /> <Fig>{fmtNum(summary.providers)}</Fig> {t('providers')} <Sep />{' '}
            <Fig>{fmtNum(summary.held)}</Fig> {t('links')} <Sep />{' '}
            {t('latest year')} {summary.latest_year != null ? <Fig>{summary.latest_year}</Fig> : <span className="fv--unknown">{t('not collected')}</span>}
          </>
        ) : t('Which organisations work with which, and how many people from which institution are placed where.')}
        verbs={<Lnk onClick={() => openRoute(ROUTES.review({ kind: 'link' }))}>{t('Proposed links in Review')}</Lnk>}
      />

      <Rail
        after={
          <>
            <div className="instr-band">
              <div className="filter-chips">
                {VIEWS.map((v) => (
                  <button key={v} type="button" className={cx('chip', view === v && 'active')} aria-pressed={view === v} onClick={() => setView(v)}>
                    {v === 'payers' ? t('Payers') : v === 'providers' ? t('Providers') : t('Matrix')}
                  </button>
                ))}
                <span className="chip-sep" aria-hidden="true" />
                {DIMS.map((d) => {
                  const chosen = filters[d];
                  return (
                    <button
                      key={d}
                      type="button"
                      className={cx('chip', (openDim === d || chosen != null) && 'active')}
                      aria-pressed={openDim === d}
                      onClick={() => setOpenDim((o) => (o === d ? null : d))}
                    >
                      {dimLabel(d)}{chosen != null && <> · {valueLabel(d, String(chosen))}</>}
                    </button>
                  );
                })}
                <span className="chip-sep" aria-hidden="true" />
                <button type="button" className={cx('chip', filters.heldOnly && 'active')} aria-pressed={!!filters.heldOnly}
                  onClick={() => setFilters((f) => ({ ...f, heldOnly: !f.heldOnly }))}>
                  {t('held only')}
                </button>
                <button type="button" className={cx('chip', filters.group === 'regime' && 'active')} aria-pressed={filters.group === 'regime'}
                  onClick={() => setFilters((f) => ({ ...f, group: f.group === 'regime' ? undefined : 'regime' }))}>
                  {t('by regime')}
                </button>
                {anyFilter && <Lnk onClick={clearFilters}>{t('Clear filters')}</Lnk>}
              </div>
              <div className="search-wrapper ml-auto w-full max-w-sm">
                <input
                  className="search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('Find an organisation to focus…')}
                  aria-label={t('Find an organisation to focus…')}
                />
              </div>
            </div>
            {matches.length > 0 && (
              <div className="instr-band">
                <div className="filter-chips">
                  {matches.map((m) => (
                    <button key={m.id} type="button" className="chip" onClick={() => { setFocus(m.id); setQuery(''); setMatches([]); }}>
                      {m.ref && <Ref>{m.ref}</Ref>} {m.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {openDim && (
              <div className="instr-band">
                <div className="filter-chips">
                  {dimValues(openDim).map((v) => {
                    const count = flows?.facets[openDim][v];
                    const active = String(filters[openDim] ?? '') === v;
                    return (
                      <button key={v} type="button" className={cx('chip', active && 'active')} aria-pressed={active}
                        onClick={() => setFilter(openDim, active ? null : v)}>
                        {valueLabel(openDim, v)}{count != null && count > 0 && <> <Fig>{fmtNum(count)}</Fig></>}
                      </button>
                    );
                  })}
                  {dimValues(openDim).length === 0 && <span className="zone-note">{t('nothing to filter by yet')}</span>}
                </div>
              </div>
            )}
          </>
        }
      >
        <RailCell
          label={t('Payers')}
          value={summary ? <Fig>{fmtNum(summary.payers)}</Fig> : t('not collected')}
          tone={summary ? undefined : 'unknown'}
          detail={summary
            ? t('{b} bodies · {r} regimes · {o} residual', { b: fmtNum(summary.payer_bodies), r: fmtNum(summary.payer_regimes), o: fmtNum(summary.payer_residuals) })
            : t('payer, referrer and funder counterparts')}
          onClick={() => setView('payers')}
          pressed={view === 'payers'}
          go="→"
        />
        <RailCell
          label={t('Providers')}
          value={summary ? <Fig>{fmtNum(summary.providers)}</Fig> : t('not collected')}
          tone={summary ? undefined : 'unknown'}
          detail={summary ? t('of {total} organisations on the record', { total: fmtNum(summary.organisations_total) }) : t('organisations naming a link')}
          onClick={() => setView('providers')}
          pressed={view === 'providers'}
          go="→"
        />
        <RailCell
          label={t('Links held')}
          value={summary ? <Fig>{fmtNum(summary.held)}</Fig> : t('not collected')}
          tone={summary ? undefined : 'unknown'}
          detail={summary && summary.proposed > 0
            ? <Lnk onClick={() => openRoute(ROUTES.review({ kind: 'link' }))}>{t('{p} proposed', { p: fmtNum(summary.proposed) })}</Lnk>
            : t('nothing waiting in review')}
        />
        <RailCell
          label={t('People accounted')}
          value={summary?.attributed_people != null ? <Fig>{fmtNum(summary.attributed_people)}</Fig> : t('not collected')}
          tone={summary?.attributed_people != null ? undefined : 'unknown'}
          detail={summary
            ? t('{n} of {m} providers give a count', { n: fmtNum(summary.attributed_providers), m: fmtNum(summary.providers) })
            : t('participants a named payer accounts for')}
          valueTitle={t('Σ over payers of the people their named providers account for — computed, carries no mark')}
        />
        <RailCell
          label={t('Channel volumes')}
          value={summary && summary.channel_volumes > 0 ? <Fig>{fmtNum(summary.channel_volumes)}</Fig> : t('not collected')}
          tone={summary && summary.channel_volumes > 0 ? undefined : 'unknown'}
          detail={summary && summary.channel_volumes > 0
            ? t('of {m} payers · the coverage bars’ denominator', { m: fmtNum(summary.payers) })
            : t('no payer holds a channel volume yet')}
          title={t('channel.measure_participants — people who received at least one insertion or training measure in the year, read on the payer body itself')}
        />
      </Rail>

      {focus != null && (
        <NetworkZone
          organisationId={focus}
          embedded
          heading={t('Focus')}
          note={focused?.name ? <><Ref>{focused.ref}</Ref> <strong>{focused.name}</strong></> : undefined}
          onClear={() => setFocus(null)}
          after={ego && ego.edges.length > 0 ? (
            <>
              <div className="zone-head" style={{ marginTop: 12 }}>
                <span className="u-label">{t('Neighbourhood')}</span>
                <span className="zone-note">{t('this organisation and its links only; a square is a payer body; dashed is proposed, dotted is estimated; the figure is the source’s own')}</span>
              </div>
              <Sheet clip>
                <NetGraph
                  nodes={ego.nodes}
                  edges={ego.edges}
                  focus={`org:${focus}`}
                  onFocus={(id) => {
                    const n = ego.nodes.find((x) => x.id === id);
                    if (n?.organisation_id != null) setFocus(n.organisation_id);
                  }}
                />
              </Sheet>
            </>
          ) : undefined}
        />
      )}

      {error && !flows ? (
        <Zone><p className="zone-empty">{t('The network could not be read: {error}', { error })}</p></Zone>
      ) : !flows ? (
        <Zone><p className="zone-empty">{t('Loading…')}</p></Zone>
      ) : flows.summary.held === 0 && flows.summary.proposed === 0 ? (
        <Zone>
          <ZoneHead label={t('Payers')} />
          <p className="zone-empty">
            {anyFilter
              ? t('No link matches these filters. Clear one to widen the slice.')
              : t('No link is on the record yet. A research run files the payers, mandants and federations an organisation’s pages name; an operator declares one from an organisation’s Network zone.')}
          </p>
        </Zone>
      ) : view === 'payers' ? (
        <>
          <Zone id="payers">
            <ZoneHead
              label={t('Payers')}
              count={<Fig>{fmtNum(payerRows.payers.length)}</Fig>}
              note={t('who pays for whom: a bar is a length on a zero baseline; the dotted rest is the channel’s volume nobody has accounted for')}
              endNote={yearWord ? <>{t('Σ within')} <Fig>{yearWord}</Fig></> : t('each Σ within the payer’s own latest year')}
            />
            <Sheet clip>
              <Tbl>
                <thead>
                  <tr>
                    {th(t('Payer'), 'name', payerSort, setPayerSort)}
                    {th(t('Providers'), 'providers', payerSort, setPayerSort, true)}
                    {th(t('People accounted'), 'people', payerSort, setPayerSort, true)}
                    {anyVolume && th(t('Coverage of the channel’s volume'), 'coverage', payerSort, setPayerSort)}
                    {th(t('Places'), null, payerSort, setPayerSort, true)}
                    {th(t('CHF'), null, payerSort, setPayerSort, true)}
                    {th(t('Year'), 'year', payerSort, setPayerSort, true)}
                    {th(t('Claim'), null, payerSort, setPayerSort)}
                  </tr>
                </thead>
                <tbody>
                  {payerRows.payers.map((p) => (
                    <FragmentRows key={p.key}>
                      {payerLine(p)}
                      {p.members?.map((m) => payerLine(m, true))}
                    </FragmentRows>
                  ))}
                </tbody>
              </Tbl>
              <div className="sheet-foot">
                <span>
                  {anyVolume
                    ? t('{n} of {m} payers hold a channel volume', { n: fmtNum(flows.summary.channel_volumes), m: fmtNum(payerRows.payers.length) })
                    : <span className="fv--unknown">{t('no payer holds channel.measure_participants yet — the coverage column appears once a run reads one')}</span>}
                </span>
                <Sep />
                <span>{t('{n} of {m} providers give a count', { n: fmtNum(flows.summary.attributed_providers), m: fmtNum(flows.summary.providers) })}</span>
                <Sep />
                <span>{t('a percentage is never summed across organisations; places and CHF are Σ within one year')}</span>
              </div>
            </Sheet>
          </Zone>
          {payerRows.others.length > 0 && (
            <Zone id="counterparts">
              <ZoneHead
                label={t('Other counterparts')}
                count={<Fig>{fmtNum(payerRows.others.length)}</Fig>}
                note={t('umbrellas, partners, accreditors, vendors, consultants, auditors — relations without a flow of people or money')}
              />
              <Sheet clip>
                <Tbl>
                  <thead>
                    <tr>
                      <th>{t('Counterpart')}</th>
                      <th>{t('Role')}</th>
                      <th className="num">{t('Providers')}</th>
                      <th>{t('Claim')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payerRows.others.map((p) => (
                      <tr
                        key={p.key}
                        className={cx(p.organisation_id != null && 'row--click', focus != null && focus === p.organisation_id && 'row--focus')}
                        onClick={p.organisation_id != null ? () => setFocus(p.organisation_id) : undefined}
                      >
                        <td>{p.kind === 'organisation' ? <><Ref>{p.ref}</Ref> {p.name}</> : p.name}</td>
                        <td>{p.roles.map((r) => linkLabel(nl, 'role', r)).join(' · ')}</td>
                        <td className="num"><Fig>{fmtNum(p.providers)}</Fig></td>
                        <td>{claimCell(p.held, p.proposed, p.estimated)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Tbl>
              </Sheet>
            </Zone>
          )}
        </>
      ) : view === 'providers' ? (
        <Zone id="providers">
          <ZoneHead
            label={t('Providers')}
            count={<Fig>{fmtNum(providerRows.length)}</Fig>}
            note={t('one row per organisation naming a link; the mix is a 100 % stacked bar of its participants (or revenue) by payer, largest first, the dotted rest unattributed')}
          />
          <Sheet clip>
            <Tbl>
              <thead>
                <tr>
                  {th(t('Provider'), 'name', providerSort, setProviderSort)}
                  {th(t('Payers named'), 'payers', providerSort, setProviderSort, true)}
                  {th(t('Payer mix'), null, providerSort, setProviderSort)}
                  {th(t('attributed'), 'attributed', providerSort, setProviderSort, true)}
                  {th(t('Clients per year'), 'clients', providerSort, setProviderSort, true)}
                  {th(t('Year'), 'year', providerSort, setProviderSort, true)}
                  {th(t('Claim'), null, providerSort, setProviderSort)}
                </tr>
              </thead>
              <tbody>
                {providerRows.map((p) => {
                  const off = p.attributed_pct == null ? 0 : Math.abs(p.attributed_pct - 100);
                  const q = p.clients_per_year;
                  return (
                    <tr
                      key={p.key}
                      className={cx('row--click', focus === p.organisation_id && 'row--focus')}
                      onClick={() => setFocus(p.organisation_id)}
                    >
                      <td>
                        <Ref>{p.ref}</Ref> {p.name}
                        <span className="fd">
                          {[p.subsector ? labelFor(lang as RasterLang, 'identity', 'subsector', p.subsector) : null, p.canton, ...p.regimes.map((r) => linkLabel(nl, 'regime', r))].filter(Boolean).join(' · ') || ' '}
                        </span>
                      </td>
                      <td className="num"><Fig>{fmtNum(p.payers)}</Fig></td>
                      <td>{mixOf(p)}</td>
                      <td className="num">
                        {p.attributed_pct != null
                          ? <Fig className={off > 2 ? 'fv--attend' : undefined} title={off > 2 ? t('the mix does not sum to 100') : undefined}>{p.attributed_pct} %</Fig>
                          : null}
                      </td>
                      <td className="num">
                        {q
                          ? <><Att kind="held" /> <Fig title={q.as_of ? `${t('as of')} ${fmtDate(q.as_of)}` : undefined}>{q.qualifier ? `${QUALIFIER_SIGN[q.qualifier]} ` : ''}{fmtNum(q.value)}</Fig></>
                          : <span className="fv--unknown">{t('not collected')}</span>}
                      </td>
                      <td className="num">{p.latest_year != null ? <Fig>{p.latest_year}</Fig> : <span className="fv--unknown">{t('not collected')}</span>}</td>
                      <td>{claimCell(p.held, p.proposed, p.estimated)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Tbl>
          </Sheet>
        </Zone>
      ) : (
        <Zone id="matrix">
          <ZoneHead
            label={t('Matrix')}
            count={<><Fig>{fmtNum(flows.providers.length)}</Fig> × <Fig>{fmtNum(payerRows.payers.length)}</Fig></>}
            note={t('providers down, payers across; a cell’s ink is the share of the provider’s participants the payer accounts for, a figure is a count or an amount, ▪ a relation with no figure, ◇ a proposal')}
          />
          <FlowMatrix
            providers={flows.providers}
            payers={payerRows.payers}
            cells={flows.cells}
            year={filters.year ?? null}
            focus={focus}
            onFocus={(id) => setFocus(id)}
          />
        </Zone>
      )}
    </PageShell>
  );
}

/** A keyed fragment for a payer row and its member sub-rows — `tbody` wants rows, not wrappers. */
function FragmentRows({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
